// Compute the objective / orders comparison between ANY two CPLEX version
// solves (a "before" and an "after"), from their result_summary blobs. The
// PJ daily models emit the objective PER NETWORK (no single top-level score),
// so we compare network-by-network. Pure → unit-tested.
//
// (We compute this client-side rather than reading a node's precomputed
// summary_deltas, because the user can compare any two variants — not just a
// version against its own parent.)

// Which per-network objective stands in for the single headline number. First
// present wins; the daily Sankey visualizes the multi-period plan, so the 5-day
// network leads. The full per-network breakdown is always shown regardless.
const PRIMARY_NETWORK_ORDER = ['5_day', 'full', 'no_pos', 'solon_only'];

export interface NetworkDelta {
  network: string;
  beforeObjective: number | null;
  afterObjective: number | null;
  objectivePct: number | null;
  ordersDelta: number | null;
  beforeOrders: number | null;
  afterOrders: number | null;
  beforePerDay: Record<string, number> | null;
  afterPerDay: Record<string, number> | null;
}

export interface CompareDeltas {
  perNetwork: NetworkDelta[];
  networksImproved: number | null;
  networksTotal: number | null;
  totalOrdersFilledDelta: number | null;
  objectivePct: number | null;
  primaryNetwork: string | null;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function perDay(v: unknown): Record<string, number> | null {
  if (!v || typeof v !== 'object') return null;
  const out: Record<string, number> = {};
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    if (typeof val === 'number') out[k] = val;
  }
  return Object.keys(out).length ? out : null;
}

interface NetStat {
  objective: number | null;
  orders: number | null;
  perDay: Record<string, number> | null;
}

function networkStats(summary: Record<string, unknown> | null | undefined): Map<string, NetStat> {
  const m = new Map<string, NetStat>();
  const pn = (summary as { per_network?: unknown } | null)?.per_network;
  if (!Array.isArray(pn)) return m;
  for (const e of pn) {
    if (!e || typeof e !== 'object') continue;
    const row = e as Record<string, unknown>;
    if (typeof row.network !== 'string') continue;
    m.set(row.network, {
      objective: num(row.objective),
      orders: num(row.orders_filled),
      perDay: perDay(row.orders_filled_per_day),
    });
  }
  return m;
}

export function computeDeltasFromSummaries(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
): CompareDeltas | null {
  const b = networkStats(before);
  const a = networkStats(after);

  const perNetwork: NetworkDelta[] = [];
  for (const [network, av] of a) {
    const bv = b.get(network);
    if (!bv) continue;
    const objectivePct =
      bv.objective !== null && av.objective !== null && bv.objective !== 0
        ? ((av.objective - bv.objective) / Math.abs(bv.objective)) * 100
        : null;
    perNetwork.push({
      network,
      beforeObjective: bv.objective,
      afterObjective: av.objective,
      objectivePct,
      beforeOrders: bv.orders,
      afterOrders: av.orders,
      ordersDelta:
        av.orders !== null && bv.orders !== null ? av.orders - bv.orders : null,
      beforePerDay: bv.perDay,
      afterPerDay: av.perDay,
    });
  }

  const totalBefore = num((before as { total_orders_filled?: unknown } | null)?.total_orders_filled);
  const totalAfter = num((after as { total_orders_filled?: unknown } | null)?.total_orders_filled);
  const totalOrdersFilledDelta =
    totalBefore !== null && totalAfter !== null ? totalAfter - totalBefore : null;

  if (!perNetwork.length && totalOrdersFilledDelta === null) return null;

  const networksImproved = perNetwork.filter(
    (r) =>
      r.beforeObjective !== null && r.afterObjective !== null && r.afterObjective > r.beforeObjective,
  ).length;

  const byName = new Map(perNetwork.map((r) => [r.network, r]));
  const primaryNetwork =
    PRIMARY_NETWORK_ORDER.find((n) => byName.has(n)) ?? perNetwork[0]?.network ?? null;
  const head = primaryNetwork ? byName.get(primaryNetwork) : null;

  return {
    perNetwork,
    networksImproved: perNetwork.length ? networksImproved : null,
    networksTotal: perNetwork.length ? perNetwork.length : null,
    totalOrdersFilledDelta,
    objectivePct: head?.objectivePct ?? null,
    primaryNetwork,
  };
}

export function formatPct(p: number | null): string {
  return p === null ? '—' : `${p >= 0 ? '+' : ''}${p.toFixed(2)}%`;
}

export function formatObjective(v: number | null): string {
  if (v === null) return '—';
  return Math.abs(v) >= 1000 ? Math.round(v).toLocaleString() : String(v);
}

export function formatPerDay(d: Record<string, number> | null): string {
  if (!d) return '—';
  const parts = Object.entries(d)
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}:${v}`);
  return parts.length ? parts.join(' ') : '0';
}
